import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, Eye } from 'lucide-react';
import { trackBlogListView, trackBlogClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const observedBlogs = useRef(new Set()); // Track which blogs have been viewed

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Setup IntersectionObserver for tracking blog card views
  useEffect(() => {
    if (blogs.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const blogSlug = entry.target.dataset.blogSlug;
            const blogTitle = entry.target.dataset.blogTitle;
            
            // Track only once per session
            if (blogSlug && !observedBlogs.current.has(blogSlug)) {
              observedBlogs.current.add(blogSlug);
              trackBlogListView(blogSlug, blogTitle);
            }
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    // Observe all blog cards
    const blogCards = document.querySelectorAll('[data-blog-slug]');
    blogCards.forEach((card) => observer.observe(card));

    return () => {
      blogCards.forEach((card) => observer.unobserve(card));
    };
  }, [blogs]);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs/published`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlogClick = (slug, title) => {
    trackBlogClick(slug, title);
    navigate(`/blogs/${slug}`);
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p>Loading blogs...</p>
      </div>
    );
  }

  return (
    <div className="blog-list-page">
      <div className="container">
        <div className="blog-header">
          <h1 className="heading-1">Our Blog</h1>
          <p className="body-large">Insights, guides, and updates on visa and immigration services</p>
        </div>

        {blogs.length === 0 ? (
          <div className="empty-state">
            <p className="body-large">No blogs published yet. Check back soon!</p>
          </div>
        ) : (
          <div className="blog-grid">
            {blogs.map((blog) => (
              <div 
                key={blog.id} 
                className="blog-card" 
                onClick={() => handleBlogClick(blog.slug, blog.title)}
                data-blog-slug={blog.slug}
                data-blog-title={blog.title}
              >
                {blog.featured_image && (
                  <div className="blog-card-image">
                    <img 
                      src={blog.featured_image} 
                      alt={blog.title}
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="blog-card-content">
                  <h3 className="heading-3">{blog.title}</h3>
                  <div className="blog-meta">
                    <span className="blog-meta-item">
                      <User size={14} />
                      {blog.author}
                    </span>
                    <span className="blog-meta-item">
                      <Calendar size={14} />
                      {new Date(blog.published_at || blog.created_at).toLocaleDateString()}
                    </span>
                    <span className="blog-meta-item">
                      <Eye size={14} />
                      {blog.views_count} views
                    </span>
                  </div>
                  <p className="body-medium blog-excerpt">
                    {stripHtml(blog.content).substring(0, 150)}...
                  </p>
                  <button className="btn-text">Read More →</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;
